import EventBroadcaster from "@/js/Common/Events/EventBroadcaster";
import Events           from "@/js/Common/Events/Events";
import SAPOMailConfig   from "@/SAPOMailConfig";

export { PushNotificationsControllerSingleton as default };

/**
* Handles Push Notifications
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class PushNotificationsController {
    /**
     * Socket io connection
     *
     * @param {Object}
     *
     * @public
     *
     * @since 7.0.0
     */
    connection;

    /**
     * Connection status
     *
     * @param {String|null}
     *
     * @public
     *
     * @since 7.0.5
     */
    status = 'PENDING';

    /**
     * Number of connection attempts
     *
     * @param {Number}
     *
     * @public
     *
     * @since 7.0.0
     */
    connection_attempts = 0;

    /**
     * Maximum number of connection attempts
     *
     * @param {Number}
     *
     * @public
     *
     * @since 7.0.0
     */
    max_connection_attempts = 10;

    /**
     * Maximum delay between connection attempts
     *
     * @param {Number}
     *
     * @public
     *
     * @since 7.0.0
     */
    max_connection_delay = 15000;

    /**
     * Connection status
     *
     * @param {Boolean}
     *
     * @public
     *
     * @since 7.0.0
     */
    connected = false;

    /**
     * Starts the websocket connection to the push notification service
     * and adds all the needed events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    start() {
        if (this.isDisabled()) {
            return;
        }

        this.connect();
        this.addConnectionEvent('connect', this.onConnect);
        this.addConnectionEvent('disconnect', this.onDisconnect);
        this.addConnectionEvent('error', this.onError);
        this.addConnectionEvent('newEvent', this.onMessage);
    }

    /**
     * Connects to the websocket service
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    connect() {
        this.setStatus('CONNECTING');
        this.increaseConnectionAttempts();
        this.connection = io.connect(
            SAPOMailConfig.server.web_sockets.push_notifications.url,
            SAPOMailConfig.server.web_sockets.push_notifications.options
        );
    }

    isDisabled() {
        if (typeof io === 'undefined') {
            this.setStatus('DISABLED');
            return true;
        }

        return false;
    }

    /**
     * Adds an event to the IO Socket Connection object
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    addConnectionEvent(event, callback) {
        PushNotificationsControllerSingleton.connection.on(event, callback);
    }

    /**
     * Handles connection events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onConnect(event) {
        PushNotificationsControllerSingleton.setStatus('CONNECTED');
        PushNotificationsControllerSingleton.resetConnectionAttempts();
    }

    /**
     * Handles disconnection events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onDisconnect(event) {
        PushNotificationsControllerSingleton.setStatus('DISCONNECTED');
        PushNotificationsControllerSingleton.retryConnection();
    }

    /**
     * Handles error events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onError(event) {
        PushNotificationsControllerSingleton.setStatus('ERROR');
        PushNotificationsControllerSingleton.retryConnection();
    }

    /**
     * Handles message events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    onMessage(event) {
        if (!event.type) {
            return;
        }

        event.folder = btoa(event.folder).replace(/=/g, "");

        if (event.type === 'newMail') {
            EventBroadcaster.broadcast(Events.PUSH_NOTIFICATIONS_MESSAGE, event);
        }
    }

    /**
     * Sets the connection status
     *
     * @param {String} status
     *
     * @public
     *
     * @since 7.0.5
     *
     * @return {void}
     */
    setStatus(status) {
        this.status = status;
    }

    /**
     * Resets the connection attempts counter
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    resetConnectionAttempts() {
        this.connection_attempts = 0;
    }

    /**
     * Increases the connection attempts counter
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    increaseConnectionAttempts() {
        this.connection_attempts++;
    }

    /**
     * Retries the connection to the websocket service
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    retryConnection() {
        if (this.status === 'CONNECTED' || this.status === 'CONNECTING' || this.status === 'DISABLED') {
            return;
        }

        if (this.connection_attempts >= this.max_connection_attempts) {
            this.setStatus('FAILED');
            return;
        }

        this.connect();

        setTimeout(() => {
            this.retryConnection();
        }, this.max_connection_delay);
    }
}

const PushNotificationsControllerSingleton = new PushNotificationsController();